import { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Tooltip,
  Alert, CircularProgress, TextField, MenuItem, Select, InputAdornment,
  LinearProgress, Tabs, Tab, Avatar,
} from '@mui/material';
import {
  Refresh, Payment, Receipt, Search, FileDownload,
  Visibility, TrendingDown, Warning, CheckCircle,
  AccountBalance, AccessTime, ErrorOutline,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (v) => `$${parseFloat(v || 0).toLocaleString('es-EC', { minimumFractionDigits: 2 })}`;
const pct = (v) => `${parseFloat(v || 0).toFixed(1)}%`;

const EDAD_COLOR = (dias) => {
  if (dias <= 30) return { bg: '#e8f5e9', text: '#2e7d32', border: '#4caf50' };
  if (dias <= 60) return { bg: '#fff8e1', text: '#f57f17', border: '#ffc107' };
  if (dias <= 90) return { bg: '#fff3e0', text: '#e65100', border: '#ff9800' };
  return { bg: '#fce4ec', text: '#b71c1c', border: '#f44336' };
};

const PIE_COLORS = ['#4caf50', '#ffc107', '#ff9800', '#f44336'];

function SemaforoGlobal({ estado }) {
  const map = {
    SALUDABLE: { color: '#2e7d32', bg: '#e8f5e9', icon: <CheckCircle />, label: 'Obligaciones al día' },
    EN_RIESGO: { color: '#e65100', bg: '#fff3e0', icon: <Warning />,      label: 'Obligaciones en riesgo' },
    CRITICO:   { color: '#b71c1c', bg: '#fce4ec', icon: <ErrorOutline />, label: 'Obligaciones críticas' },
  };
  const s = map[estado] || map.SALUDABLE;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, bgcolor: s.bg, border: `1px solid ${s.color}` }}>
      <Box sx={{ color: s.color, display: 'flex' }}>{s.icon}</Box>
      <Typography sx={{ color: s.color, fontWeight: 700, fontSize: 13 }}>{s.label}</Typography>
    </Box>
  );
}

function Kpi({ icon, label, value, sub, color = '#e65100', bg = '#fff3e0' }) {
  return (
    <Paper sx={{ p: 2, borderRadius: 2, borderTop: `3px solid ${color}`, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: 'text.secondary' }}>{label}</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color, mt: 0.25 }}>{value}</Typography>
          {sub && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{sub}</Typography>}
        </Box>
        <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>{icon}</Box>
      </Box>
    </Paper>
  );
}

export default function CuentasPorPagar() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingAnalisis, setLoadingAnalisis] = useState(false);
  const [error, setError] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [compras, setCompras] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [analisis, setAnalisis] = useState(null);

  // Filtros
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroEdad, setFiltroEdad] = useState('todos');

  useEffect(() => { cargarDatos(); }, []);
  useEffect(() => { if (tab === 1 && !analisis) cargarAnalisis(); }, [tab]);

  const cargarDatos = async () => {
    setLoading(true); setError(null);
    try {
      const [resRes, provRes, compRes] = await Promise.all([
        axios.get(`${API}/api/cuentas-pagar/resumen-obligaciones`, cfg()).catch(() => ({ data: { data: null } })),
        axios.get(`${API}/api/cuentas-pagar/proveedores-con-saldo`, cfg()).catch(() => ({ data: { data: [] } })),
        axios.get(`${API}/api/cuentas-pagar/compras-pendientes`, cfg()).catch(() => ({ data: { data: [] } })),
      ]);

      setResumen(resRes.data?.data || resRes.data);
      setProveedores(provRes.data?.data || []);

      const hoy = new Date();
      const parseFecha = (s) => {
        if (!s) return null;
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) { const [d,m,y]=s.split('/'); return new Date(+y,+m-1,+d); }
        const d = new Date(s); return isNaN(d) ? null : d;
      };

      const pendientes = (compRes.data?.data || []).map(c => {
        const total  = parseFloat(c.totales?.importeTotal || c.total || 0);
        const saldo  = parseFloat(c.saldo_pendiente ?? total);
        const pagado = total - saldo;
        const fecha  = parseFecha(c.fecha_emision) || parseFecha(c.created_at);
        const dias   = fecha ? Math.floor((hoy - fecha) / 86400000) : 0;
        return { ...c, total, saldo, pagado, fecha, dias };
      }).filter(c => c.saldo > 0.01).sort((a, b) => b.dias - a.dias);

      setCompras(pendientes);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const cargarAnalisis = async () => {
    setLoadingAnalisis(true);
    try {
      const r = await axios.get(`${API}/api/cuentas-pagar/analisis`, cfg());
      if (r.data.success) setAnalisis(r.data.data);
    } catch (_) {}
    finally { setLoadingAnalisis(false); }
  };

  // Filtrado
  const comprasFiltradas = useMemo(() => {
    return compras.filter(c => {
      const q = busqueda.toLowerCase();
      const nombre = c.proveedor?.razonSocial || c.proveedor?.razon_social || '';
      const ruc    = c.proveedor?.ruc || '';
      const num    = c.factura?.numeroComprobante || '';
      const matchQ = !q || nombre.toLowerCase().includes(q) || ruc.includes(q) || num.includes(q);
      const matchE = filtroEstado === 'todos' || (filtroEstado === 'parcial' ? c.pagado > 0 : filtroEstado === 'pendiente' ? c.pagado === 0 : true);
      const matchA = filtroEdad === 'todos' || (filtroEdad === '0-30' ? c.dias <= 30 : filtroEdad === '31-60' ? c.dias > 30 && c.dias <= 60 : filtroEdad === '61-90' ? c.dias > 60 && c.dias <= 90 : c.dias > 90);
      return matchQ && matchE && matchA;
    });
  }, [compras, busqueda, filtroEstado, filtroEdad]);

  const totalFiltrado = comprasFiltradas.reduce((s, c) => s + c.saldo, 0);

  const exportarCSV = () => {
    const rows = [['Fecha','Proveedor','RUC','No. Factura','Total','Pagado','Saldo','Días','Estado']];
    comprasFiltradas.forEach(c => {
      rows.push([
        c.fecha_emision || '—',
        c.proveedor?.razonSocial || c.proveedor?.razon_social || '—',
        c.proveedor?.ruc || '—',
        c.factura?.numeroComprobante || '—',
        c.total.toFixed(2), c.pagado.toFixed(2), c.saldo.toFixed(2), c.dias,
        c.pagado > 0 ? 'Parcial' : 'Pendiente',
      ]);
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `cuentas_pagar_${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>

      {/* HEADER */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Cuentas por Pagar</Typography>
          <Typography variant="body2" color="text.secondary">Gestión de obligaciones y seguimiento de pagos</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={cargarDatos} size="small">Actualizar</Button>
          <Button variant="outlined" startIcon={<FileDownload />} onClick={exportarCSV} size="small">Exportar CSV</Button>
          <Button variant="contained" startIcon={<Payment />} onClick={() => navigate('/cuentas-pagar/registrar-pago')}
            sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }}>Registrar Pago</Button>
          <Button variant="outlined" onClick={() => navigate('/cuentas-pagar/pagos')}>Ver Pagos</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* KPIs */}
      {resumen && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 20 }}>
          <Kpi icon={<AccountBalance />} label="Total Comprado"    value={fmt(resumen.total_comprado)}    sub={`${resumen.cantidad_compras||0} compras`}     color="#1565c0" bg="#e3f2fd" />
          <Kpi icon={<CheckCircle />}   label="Total Pagado"      value={fmt(resumen.total_pagado)}       sub="Obligaciones saldadas"                        color="#2e7d32" bg="#e8f5e9" />
          <Kpi icon={<TrendingDown />}  label="Saldo Pendiente"   value={fmt(resumen.saldo_pendiente)}    sub={`${resumen.compras_pendientes||0} pendientes`} color="#e65100" bg="#fff3e0" />
          <Kpi icon={<Warning />}       label="Vencido +90 días"  value={fmt(resumen.antiguedad_saldos?.['90+'])} sub="Alto riesgo"                         color="#b71c1c" bg="#fce4ec" />
          <Kpi icon={<AccessTime />}    label="% Pagado"
            value={resumen.total_comprado > 0 ? pct((resumen.total_pagado / resumen.total_comprado) * 100) : '0%'}
            sub="Tasa de pago" color="#6a1b9a" bg="#f3e5f5" />
        </div>
      )}

      {/* PESTAÑAS */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Tab label="📋 Obligaciones" />
        <Tab label="📊 Análisis" />
        <Tab label="🏭 Por Proveedor" />
      </Tabs>

      {/* ══ TAB 0: OBLIGACIONES ══ */}
      {tab === 0 && (
        <>
          <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" placeholder="Buscar proveedor, RUC, factura..." value={busqueda}
              onChange={e => setBusqueda(e.target.value)} sx={{ minWidth: 280 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
            <Select size="small" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} sx={{ minWidth: 130 }}>
              <MenuItem value="todos">Todos los estados</MenuItem>
              <MenuItem value="pendiente">Sin pago</MenuItem>
              <MenuItem value="parcial">Pago parcial</MenuItem>
            </Select>
            <Select size="small" value={filtroEdad} onChange={e => setFiltroEdad(e.target.value)} sx={{ minWidth: 140 }}>
              <MenuItem value="todos">Toda antigüedad</MenuItem>
              <MenuItem value="0-30">0-30 días</MenuItem>
              <MenuItem value="31-60">31-60 días</MenuItem>
              <MenuItem value="61-90">61-90 días</MenuItem>
              <MenuItem value="+90">+90 días</MenuItem>
            </Select>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              {comprasFiltradas.length} doc. · Pendiente: <strong style={{ color: '#d32f2f' }}>{fmt(totalFiltrado)}</strong>
            </Typography>
          </Paper>

          <Paper sx={{ borderRadius: 2 }}>
            <Box sx={{ px: 2, py: 1.5, bgcolor: '#e65100', color: '#fff', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Receipt sx={{ fontSize: 20 }} />
              <Typography fontWeight={700}>Facturas Pendientes de Pago</Typography>
              <Chip label={comprasFiltradas.length} size="small" sx={{ bgcolor: '#fff', color: '#e65100', fontWeight: 700, ml: 1 }} />
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f7fa', '& th': { fontWeight: 700, fontSize: 11, py: 1 } }}>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Proveedor</TableCell>
                    <TableCell>RUC</TableCell>
                    <TableCell>No. Factura</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Pagado</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                    <TableCell sx={{ minWidth: 110 }}>Progreso</TableCell>
                    <TableCell align="center">Antigüedad</TableCell>
                    <TableCell>Vencimiento</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comprasFiltradas.length === 0 ? (
                    <TableRow><TableCell colSpan={12} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">No hay facturas con los filtros seleccionados</Typography>
                    </TableCell></TableRow>
                  ) : (
                    comprasFiltradas.map((c, i) => {
                      const ec = EDAD_COLOR(c.dias);
                      const progreso = c.total > 0 ? Math.round((c.pagado / c.total) * 100) : 0;
                      const provRuc = c.proveedor?.ruc || '';
                      const diasCredito = c.condiciones_pago?.dias_credito || 0;
                      const fechaVenc = c.condiciones_pago?.fecha_vencimiento;
                      const vencida = fechaVenc && new Date(fechaVenc) < new Date();
                      return (
                        <TableRow key={c.id} hover
                          sx={{ bgcolor: i % 2 === 0 ? '#fff' : '#fafafa', '& td': { fontSize: 12, py: 0.8 } }}>
                          <TableCell>{c.fecha_emision || new Date(c.created_at).toLocaleDateString('es-EC')}</TableCell>
                          <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.proveedor?.razonSocial || c.proveedor?.razon_social || '—'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{provRuc}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{c.factura?.numeroComprobante || '—'}</TableCell>
                          <TableCell align="right">{fmt(c.total)}</TableCell>
                          <TableCell align="right" sx={{ color: '#2e7d32' }}>{fmt(c.pagado)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: '#d32f2f' }}>{fmt(c.saldo)}</TableCell>
                          <TableCell sx={{ minWidth: 110 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LinearProgress variant="determinate" value={progreso}
                                sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: progreso === 100 ? '#4caf50' : progreso > 0 ? '#ffc107' : '#f44336' } }} />
                              <Typography sx={{ fontSize: 10, minWidth: 28, color: 'text.secondary' }}>{progreso}%</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1, py: 0.25, borderRadius: 1, bgcolor: ec.bg, border: `1px solid ${ec.border}` }}>
                              <Typography sx={{ fontSize: 11, fontWeight: 700, color: ec.text }}>{c.dias}d</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {diasCredito > 0
                              ? <Chip label={`${diasCredito}d`} color={vencida ? 'error' : 'warning'} size="small" sx={{ fontSize: 10, height: 20 }} />
                              : <Chip label="Contado" size="small" variant="outlined" sx={{ fontSize: 10, height: 20 }} />}
                          </TableCell>
                          <TableCell>
                            {vencida
                              ? <Chip label="Vencida"  color="error"   size="small" sx={{ fontSize: 10, height: 20 }} />
                              : c.pagado > 0
                              ? <Chip label="Parcial"  color="warning" size="small" sx={{ fontSize: 10, height: 20 }} />
                              : <Chip label="Pendiente" color="info"   size="small" sx={{ fontSize: 10, height: 20 }} />}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Registrar Pago">
                              <IconButton size="small" color="success"
                                onClick={() => navigate(`/cuentas-pagar/registrar-pago?proveedorId=${provRuc}`)}>
                                <Payment fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Estado de Cuenta">
                              <IconButton size="small"
                                onClick={() => navigate(`/cuentas-pagar/estado-cuenta/${provRuc || c.proveedor?.id}`)}>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {comprasFiltradas.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: '#fff3e0', borderTop: '2px solid #e65100' }}>
                <Typography variant="body2" fontWeight={700}>{comprasFiltradas.length} factura(s) pendiente(s)</Typography>
                <Typography variant="body2" fontWeight={700} color="error.main">Total: {fmt(totalFiltrado)}</Typography>
              </Box>
            )}
          </Paper>
        </>
      )}

      {/* ══ TAB 1: ANÁLISIS ══ */}
      {tab === 1 && (
        <>
          {loadingAnalisis ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : analisis ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto repeat(4,1fr)', gap: 12, marginBottom: 20, alignItems: 'center' }}>
                <SemaforoGlobal estado={analisis.semaforo} />
                <Kpi icon={<TrendingDown />} label="% Pendiente"       value={pct(analisis.tasaPendiente)}  color="#b71c1c" bg="#fce4ec" />
                <Kpi icon={<CheckCircle />}  label="% Pagado"          value={pct(analisis.tasaPago)}       color="#2e7d32" bg="#e8f5e9" />
                <Kpi icon={<AccessTime />}   label="Días prom. pago"   value={`${analisis.diasPromedioPago}d`} color="#1565c0" bg="#e3f2fd" />
                <Kpi icon={<Warning />}      label="Saldo vencido +90" value={fmt(analisis.saldoVencido)}   color="#e65100" bg="#fff3e0" />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '60% 38%', gap: 16, marginBottom: 20 }}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={700} gutterBottom>📉 Tendencia de Pagos ({new Date().getFullYear()})</Typography>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={analisis.tendencia} margin={{ left: 8, right: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="mes" fontSize={11} />
                      <YAxis fontSize={11} tickFormatter={v => `$${v}`} width={64} />
                      <RTooltip formatter={(v, n) => [`$${v.toFixed(2)}`, n]} />
                      <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="comprado"  name="Comprado"  fill="#90caf9" radius={[2,2,0,0]} />
                      <Bar dataKey="pagado"    name="Pagado"    fill="#4caf50" radius={[2,2,0,0]} />
                      <Bar dataKey="pendiente" name="Pendiente" fill="#f44336" radius={[2,2,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={700} gutterBottom>⏳ Distribución por Antigüedad</Typography>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={analisis.distAntiguedad.filter(d => d.valor > 0)} dataKey="valor" nameKey="rango"
                        cx="50%" cy="50%" outerRadius={70}
                        label={({ rango, percent }) => `${rango} ${(percent*100).toFixed(0)}%`}
                        labelLine={false} fontSize={10}>
                        {analisis.distAntiguedad.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <RTooltip formatter={(v) => fmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{ mt: 1 }}>
                    {analisis.distAntiguedad.map((d, i) => (
                      <Box key={d.rango} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PIE_COLORS[i] }} />
                          <Typography variant="caption">{d.rango} días</Typography>
                        </Box>
                        <Typography variant="caption" fontWeight={700}>{fmt(d.valor)}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </div>

              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={700} gutterBottom>🔴 Top Proveedores con Mayor Deuda</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f9fafb' }}>
                      {['#','Proveedor','RUC','Compras','Saldo Pendiente','Días máx.','Riesgo'].map(h => (
                        <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, py: 0.75 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analisis.topProveedores.map((p, i) => {
                      const ec = EDAD_COLOR(p.diasMaximo);
                      return (
                        <TableRow key={p.ruc} hover>
                          <TableCell>
                            <Avatar sx={{ width: 26, height: 26, fontSize: 12, bgcolor: i === 0 ? '#d32f2f' : i < 3 ? '#f57f17' : '#e65100' }}>
                              {i+1}
                            </Avatar>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>{p.nombre}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{p.ruc}</TableCell>
                          <TableCell align="center">{p.compras}</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#d32f2f' }}>{fmt(p.saldo)}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'inline-flex', px: 1, py: 0.25, borderRadius: 1, bgcolor: ec.bg, border: `1px solid ${ec.border}` }}>
                              <Typography sx={{ fontSize: 11, fontWeight: 700, color: ec.text }}>{p.diasMaximo}d</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {p.diasMaximo > 90 ? <Chip label="Crítico" color="error"   size="small" sx={{ fontSize: 10, height: 20 }} />
                            : p.diasMaximo > 60 ? <Chip label="Alto"    color="warning" size="small" sx={{ fontSize: 10, height: 20 }} />
                            : p.diasMaximo > 30 ? <Chip label="Medio"   color="info"    size="small" sx={{ fontSize: 10, height: 20 }} />
                                                : <Chip label="Bajo"    color="success" size="small" sx={{ fontSize: 10, height: 20 }} />}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">No hay datos de análisis disponibles</Typography>
            </Box>
          )}
        </>
      )}

      {/* ══ TAB 2: POR PROVEEDOR ══ */}
      {tab === 2 && (
        <Paper sx={{ borderRadius: 2 }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: '#e65100', color: '#fff', borderRadius: '8px 8px 0 0' }}>
            <Typography fontWeight={700}>Proveedores con Saldo Pendiente</Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f7fa', '& th': { fontWeight: 700, fontSize: 11, py: 1 } }}>
                <TableCell>Proveedor</TableCell>
                <TableCell>RUC</TableCell>
                <TableCell align="center">Compras Pend.</TableCell>
                <TableCell align="right">Saldo Total</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Indicador</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {proveedores.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">No hay proveedores con saldo pendiente</Typography>
                </TableCell></TableRow>
              ) : (
                proveedores.map(p => {
                  const maxSaldo = Math.max(...proveedores.map(x => parseFloat(x.saldo_total || 0)));
                  const pct2 = maxSaldo > 0 ? (parseFloat(p.saldo_total || 0) / maxSaldo) * 100 : 0;
                  return (
                    <TableRow key={p.proveedor_id} hover sx={{ '& td': { fontSize: 12, py: 0.9 } }}>
                      <TableCell sx={{ fontWeight: 600 }}>{p.proveedor_nombre}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{p.proveedor_ruc}</TableCell>
                      <TableCell align="center">
                        <Chip label={p.compras_pendientes} size="small" color="error" sx={{ fontWeight: 700 }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#d32f2f' }}>{fmt(p.saldo_total)}</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        <LinearProgress variant="determinate" value={pct2}
                          sx={{ height: 8, borderRadius: 4, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: pct2 > 75 ? '#f44336' : pct2 > 40 ? '#ff9800' : '#4caf50' } }} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Estado de Cuenta">
                          <IconButton size="small" color="primary"
                            onClick={() => navigate(`/cuentas-pagar/estado-cuenta/${p.proveedor_id}`)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Registrar Pago">
                          <IconButton size="small" color="success"
                            onClick={() => navigate(`/cuentas-pagar/registrar-pago?proveedorId=${p.proveedor_id}`)}>
                            <Payment fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

    </Box>
  );
}
